import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import Captcha from "./Captcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EnquireDialog — single source-of-truth contact form rendered as a modal.
 * Used by both the public Header (`Enquire` CTA) and the product detail page
 * (`Enquire about this product`). POSTs to the same `/api/public/inquiries`
 * endpoint that the Contact page uses.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   productName — optional. When provided, pre-fills the subject + message and
 *                shows a small "About: …" pill above the form.
 *   source     — short identifier persisted on the inquiry (e.g. "header",
 *                "product-detail"). Stored in the `subject` if not pre-filled.
 */
export default function EnquireDialog({ open, onClose, productName, source = "header" }) {
  const initial = () => ({
    name: "",
    email: "",
    phone: "",
    subject: productName ? `Enquiry: ${productName}` : "",
    message: productName ? `I'd like to enquire about ${productName}. Please share more details.\n\n` : "",
  });
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [flying, setFlying] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState({ token: "", answer: "" });
  const [captchaKey, setCaptchaKey] = useState(0);
  const firstFieldRef = useRef(null);

  // Reset whenever the dialog re-opens (with possibly different productName)
  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: productName ? `Enquiry: ${productName}` : "",
        message: productName ? `I'd like to enquire about ${productName}. Please share more details.\n\n` : "",
      });
      setSubmitted(false);
      setFlying(false);
      const t = setTimeout(() => firstFieldRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open, productName]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    if (!captcha.token || !captcha.answer) {
      toast.error("Please solve the CAPTCHA");
      return;
    }
    setSubmitting(true);
    setFlying(true);
    try {
      const payload = {
        ...form,
        subject: form.subject || `Enquiry from ${source}`,
        captcha_token: captcha.token,
        captcha_answer: captcha.answer,
      };
      await Promise.all([
        axios.post(`${API}/public/inquiries`, payload),
        new Promise((res) => setTimeout(res, 1000)),
      ]);
      setSubmitted(true);
      toast.success("Message sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
      setFlying(false);
      setCaptchaKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] rounded-3xl"
        data-testid="enquire-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {productName ? "Enquire about this product" : "Get in touch"}
          </DialogTitle>
          {productName && (
            <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C2410C]/10 text-[#C2410C] text-[10px] tracking-[0.25em] uppercase font-semibold w-fit">
              About: {productName}
            </div>
          )}
          <p className="mt-2 text-sm text-[#475569]">We respond to most enquiries within one business day.</p>
        </DialogHeader>

        <div className="relative">
          {submitted ? (
            <div className="text-center py-10" data-testid="enquire-success">
              <CheckCircle size={48} weight="fill" className="mx-auto text-[#0EA5E9]" />
              <h3 className="mt-4 text-xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Thank you — we got your message</h3>
              <p className="mt-2 text-sm text-[#475569]">Our team will get back to you shortly.</p>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className={`space-y-4 transition-opacity duration-500 ${flying ? "opacity-30 pointer-events-none" : "opacity-100"}`} data-testid="enquire-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Full name *" inputRef={firstFieldRef} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="enquire-name" />
                  <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="enquire-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="enquire-phone" />
                  <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="enquire-subject" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">Message *</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition resize-none"
                    data-testid="enquire-message"
                  />
                </div>
                <Captcha
                  onChange={setCaptcha}
                  refreshKey={captchaKey}
                  testId="enquire-captcha"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold transition disabled:opacity-60 active:scale-95"
                  data-testid="enquire-submit"
                >
                  {submitting ? "Sending..." : "Send message"} <PaperPlaneTilt size={14} weight="fill" />
                </button>
              </form>

              {flying && (
                <div
                  className="pointer-events-none absolute z-10"
                  style={{
                    left: "8%", bottom: "8%",
                    animation: "paperPlaneFly 1.1s cubic-bezier(0.55, 0.1, 0.25, 1) forwards",
                  }}
                  aria-hidden
                >
                  <PaperPlaneTilt size={48} weight="fill" className="text-[#C2410C] drop-shadow-[0_8px_24px_rgba(194,65,12,0.45)]" />
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid, inputRef }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">{label}</label>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition"
        data-testid={testid}
      />
    </div>
  );
}
